import { useCallback, useState } from "react";
import classNames from "classnames";
import { useRouter } from "next/router";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import { motion } from "framer-motion";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import Image from "next/image";
//styles
import styles from "./phone.module.scss";
//component
import Input from "@/components/common/input/input";
import IconButton from "@/components/common/iconButton/iconButton";
import PrimaryButton from "@/components/common/primaryButton";
//types
import { PhonePageProps } from "./type";
import { LoginStep } from "../../types";
import { AnimationType } from "../../loginmodal";
import { ButtonType } from "@/components/common/primaryButton/types";
//apis
//utils
import { mixpanelTrack } from "@/helper/mixpanelService";
//zustand
import { useLoginStore } from "@/providers/login/login.provider";
import { useAppStore } from "@/providers/app";
import { userApi } from "@/services/api";
import { SendOtpBody, SendOtpResponse } from "@/services/types/user";
import { isUserProfileComplete } from "@/utils";

const schema = z.object({
  phone: z
    .string()
    .length(10)
    .regex(/^[5-9]\d{9}$/, "Invalid number"),
});
type FormFields = z.infer<typeof schema>;
declare global {
  interface Window {
    grecaptcha: any;
  }
}
const Phone = ({ back }: PhonePageProps) => {
  const setState = useLoginStore((state) => state.setState);
  const defaultPhone = useLoginStore((state) => state.phone);
  const setLoginModalVisibility = useAppStore(
    (state) => state.setLoginModalVisibility
  );
  const isKeyboardOpen = useDetectKeyboardOpen();
  const router = useRouter();
  const setToastVisibility = useAppStore((state) => state.setToastVisibility);
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<FormFields>({
    defaultValues: {
      phone: defaultPhone,
    },
    resolver: zodResolver(schema),
  });

  const onHandle: SubmitHandler<FormFields> = useCallback(async (data) => {
    try {
      setLoading(true);

      let body: SendOtpBody = {
        phone: data?.phone,
      };

      const response: SendOtpResponse = await userApi.sendOtp(body);

      mixpanelTrack("Signup Web - Phone Entered", { phone: data?.phone });

      setLoading(false);
      setState({
        phone: data?.phone,
        newUser:
          response?.data?.new_user === undefined ||
          !isUserProfileComplete(response?.data),
        animationType: AnimationType.SLIDEINRIGHT,
        step: LoginStep.OTP,
      });
    } catch (e) {
      console.error("Send Otp Error", e);
      setLoading(false);
      setToastVisibility({
        text: "Oops, something went wrong. Please try again in a few minutes.",
        type: "SECONDARY",
      });
    }
  }, []);

  return (
    <>
      <motion.form
        className={styles["container"]}
        onSubmit={handleSubmit(onHandle)}
      >
        <div className={styles["header"]}>
          <>
            <Image
              onClick={back}
              className={classNames(styles["cross"], styles["cursor-pointer"])}
              height={14.3}
              width={14.3}
              src={"/icons/modal_close.webp"}
              alt={"close-icon"}
            />
            <Image
              onClick={back}
              className={styles["arrow"]}
              height={22}
              width={22}
              src={"/icons/arrow-left.svg"}
              alt={"close-icon"}
            />
          </>
          <p>Login / Signup to continue</p>
        </div>
        <div className={classNames(styles["title-cont"])}>
          <p>Enter your phone number</p>
        </div>
        <div className={classNames(styles["ph-24"])}>
          <Input
            maxLength={10}
            type={"number"}
            placeholder="Phone number"
            register={register("phone")}
            inputmode="numeric"
            phone={true}
            onInput={(e) => {
              const input = e.target as HTMLInputElement; // Cast to HTMLInputElement
              input.value = input.value.slice(0, 10);
            }}
          />
        </div>

        <div
          className={classNames(
            styles["button_container"],
            isKeyboardOpen && styles["bottom-45"]
          )}
        >
          <p>
            By entering your number, you’re agreeing to our{" "}
            <span
              onClick={() => {
                router.push("/terms-of-service");
                setLoginModalVisibility(false);
              }}
            >
              Terms of Service
            </span>{" "}
            &{" "}
            <span
              onClick={() => {
                router.push("/privacy-policy");
                setLoginModalVisibility(false);
              }}
            >
              Privacy Policy.
            </span>
          </p>
          <div className={styles["circular-button"]}>
            <IconButton
              disabled={!isValid || isSubmitting}
              type="submit"
              loading={loading}
            />
          </div>
          <div className={styles["full-button"]}>
            <PrimaryButton
              buttonType={
                !isValid || isSubmitting
                  ? ButtonType.DISABLED
                  : ButtonType.PRIMARY
              }
              text={"Continue"}
              type="submit"
              loading={loading}
            />
          </div>
        </div>
      </motion.form>
    </>
  );
};

export default Phone;
